import { useState } from 'react';
import { useAuth } from '../../auth/authContext';
import { useDeskEngine, useDeskSnapshot } from '../deskContext';
import { UnsyncedModal } from './UnsyncedModal';

/** Rendered once, app-wide: shows the engine's blocker (a wipe that would
 * lose unsynced desk events) until the receptionist resolves it. */
export function DeskBlockerModal() {
  const engine = useDeskEngine();
  const { blocker } = useDeskSnapshot();
  const { signOut } = useAuth();
  const [hiddenFor, setHiddenFor] = useState<object | null>(null);

  if (!blocker || hiddenFor === blocker) return null;
  const n = blocker.events.length;
  const events = `${n} desk ${n === 1 ? 'event' : 'events'}`;
  const discard = () => engine.discardAndRestart();

  switch (blocker.reason) {
    case 'signed_out':
      return (
        <UnsyncedModal
          title="Signed out with unsynced desk events"
          body={`${events} from this desk have not reached NamibiaGo yet. They are kept, encrypted, on this device. Sign in again with the same account to sync them.`}
          events={blocker.events}
          primary={{ label: 'Sign in to sync them', onClick: () => setHiddenFor(blocker) }}
          discardLabel="Discard them"
          onDiscard={discard}
        />
      );
    case 'other_owner':
      return (
        <UnsyncedModal
          title="Another account's desk events are on this device"
          body={`${events} recorded by a different account have not synced. Sign out so that account can sign in and sync them, or download a copy and discard them.`}
          events={blocker.events}
          primary={{ label: 'Sign out', onClick: () => void signOut() }}
          discardLabel="Discard them"
          onDiscard={discard}
        />
      );
    case 'version_mismatch':
      return (
        <UnsyncedModal
          title="Desk events from an older version"
          body={`${events} were saved by an older version of the portal and can't be synced automatically. Download a copy to enter them by hand, then discard them.`}
          events={blocker.events}
          discardLabel="Discard them"
          onDiscard={discard}
        />
      );
    case 'desk_key_revoked':
      return (
        <UnsyncedModal
          title="This desk's access was revoked"
          body={`${events} could not be synced because this device's desk access was revoked. Download a copy for the property owner, then discard them.`}
          events={blocker.events}
          discardLabel="Discard them"
          onDiscard={async () => {
            await discard();
            await signOut();
          }}
        />
      );
  }
}
